import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { api } from "../api/client";
import ImageModal from "../components/ImageModal";
import LoadingSpinner from "../components/LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";
import { getStandardComments } from "../constants/standardComments";

const severityColor = {
    critical: "var(--red)",
    high: "#f97316",
    warning: "#eab308",
    medium: "#3b82f6",
    low: "var(--text-muted)",
    info: "var(--text-muted)",
};

function ScoreBadge({ score }) {
    if (score == null) return <span style={{ color: "var(--text-muted)" }}>--</span>;
    const color = score >= 8 ? "#22c55e" : score >= 5 ? "#eab308" : "var(--red)";
    return <span style={{ color, fontWeight: 600 }}>{score}/10</span>;
}

// --- Create Issue Form ---
function CreateIssueForm({ finding, preset, siteId, scanHour, onCreated, onCancel }) {
    const { accounts } = useMsal();
    const account = accounts[0];
    const [routingOptions, setRoutingOptions] = useState(null);
    const [assignTo, setAssignTo] = useState("Site");
    const [comment, setComment] = useState("");
    const [title, setTitle] = useState("");
    const [cc, setCc] = useState("");
    const [sendToSelf, setSendToSelf] = useState(false);
    const [severity, setSeverity] = useState(finding?.severity || "warning");
    const [includePhoto, setIncludePhoto] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const isPresetLevel = !finding && !!preset;

    // Standard comments, restricted to this fixture — see constants/standardComments.js
    const commentGroups = useMemo(
        () => getStandardComments(preset?.fixtureType || finding?.fixtureType),
        [preset?.fixtureType, finding?.fixtureType]
    );

    const applyStandardComment = (text) => {
        if (!text) return;
        setComment(prev => (prev.trim() ? `${prev.replace(/\s+$/, "")}\n${text}` : text));
    };

    useEffect(() => {
        api.routingOptions(siteId)
            .then(data => setRoutingOptions(data))
            .catch(err => setError("Failed to load routing options: " + err.message));
    }, [siteId]);

    const selectedTeam = routingOptions?.teams?.find(t => t.team === assignTo);
    const emailWillDefer = assignTo === "Site" && !!scanHour;

    const handleSubmit = async () => {
        if (isPresetLevel && !title.trim() && !comment.trim()) {
            setError("Please enter a title or comment describing the issue");
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            let result;
            const reviewerEmail = account?.username || "web_user";
            const shared = {
                assignTo,
                comment,
                severity,
                siteId,
                includePhoto,
                cc: cc.trim() || undefined,
                sendToSelf: sendToSelf ? reviewerEmail : undefined,
                reviewerEmail,
                reviewerName: account?.name || account?.username || "Web User",
                scanHour: scanHour || undefined,
            };
            if (isPresetLevel) {
                result = await api.createIssueFromPreset({
                    assessmentId: preset.id,
                    title: title.trim() || `${preset.checkType || preset.presetName} — issue reported`,
                    ...shared,
                });
            } else {
                result = await api.createIssueFromFinding({
                    findingIds: [finding.id],
                    ...shared,
                });
            }
            onCreated(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{
            background: "var(--bg-primary)", border: "1px solid var(--primary)",
            borderRadius: 8, padding: 16, marginTop: 8
        }}>
            <div style={{ fontWeight: 600, marginBottom: 12, color: "var(--text-primary)" }}>
                {isPresetLevel ? "Create Issue from Scan" : "Create Issue from Finding"}
            </div>

            <div style={{ display: "grid", gap: 10 }}>
                {isPresetLevel && (
                    <div>
                        <label style={{ display: "block", fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>Issue Title</label>
                        <input
                            type="text"
                            className="action-input"
                            placeholder={`e.g. ${preset.checkType || preset.presetName} needs attention`}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            style={{ width: "100%", boxSizing: "border-box", padding: "6px 10px" }}
                        />
                    </div>
                )}

                <div>
                    <label style={{ display: "block", fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>Assign To</label>
                    <select
                        className="filter-select"
                        value={assignTo}
                        onChange={(e) => setAssignTo(e.target.value)}
                        style={{ width: "100%" }}
                    >
                        {(routingOptions?.teams || []).map(t => (
                            <option key={t.team} value={t.team} disabled={!t.email}>
                                {t.team} — {t.display}{!t.email ? " (no email)" : ""}
                            </option>
                        ))}
                    </select>
                    {selectedTeam && selectedTeam.email && (
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                            Email: {selectedTeam.email}
                            {emailWillDefer && " — sent when this review is closed, not now"}
                        </div>
                    )}
                </div>

                <div>
                    <label style={{ display: "block", fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>Severity</label>
                    <select
                        className="filter-select"
                        value={severity}
                        onChange={(e) => setSeverity(e.target.value)}
                        style={{ width: "100%" }}
                    >
                        {["critical", "high", "warning", "medium", "low", "info"].map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label style={{ display: "block", fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>Comment</label>
                    <select
                        className="filter-select"
                        value=""
                        onChange={(e) => { applyStandardComment(e.target.value); e.target.value = ""; }}
                        style={{ width: "100%", marginBottom: 6 }}
                    >
                        <option value="">Standard comment — pick from the list or type your own below</option>
                        {commentGroups.map(g => (
                            <optgroup key={g.label} label={g.label}>
                                {g.comments.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                    <textarea
                        className="action-input"
                        placeholder="Add context for the recipient..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={2}
                        style={{ width: "100%", boxSizing: "border-box" }}
                    />
                </div>

                <div>
                    <label style={{ display: "block", fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>CC (optional)</label>
                    <input
                        type="text"
                        className="action-input"
                        placeholder="email@example.com, another@example.com"
                        value={cc}
                        onChange={(e) => setCc(e.target.value)}
                        style={{ width: "100%", boxSizing: "border-box", padding: "6px 10px" }}
                    />
                </div>

                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={includePhoto}
                            onChange={(e) => setIncludePhoto(e.target.checked)}
                        />
                        Include photo
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={sendToSelf}
                            onChange={(e) => setSendToSelf(e.target.checked)}
                        />
                        Send copy to myself
                    </label>
                </div>

                {error && <div style={{ color: "var(--red)", fontSize: 13 }}>{error}</div>}

                <div style={{ display: "flex", gap: 8 }}>
                    <button
                        className="action-submit"
                        onClick={handleSubmit}
                        disabled={submitting || !selectedTeam?.email}
                        style={{ flex: 1 }}
                    >
                        {submitting ? "Creating..." : emailWillDefer ? "Create Issue" : "Create Issue & Send Email"}
                    </button>
                    <button className="action-cancel" onClick={onCancel}>Cancel</button>
                </div>
                {emailWillDefer && (
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8 }}>
                        Single collated email will be sent upon Close Review.
                    </div>
                )}
            </div>
        </div>
    );
}

// --- Issue Created Confirmation ---
function IssueCreatedBanner({ result }) {
    return (
        <div style={{
            background: "rgba(34,197,94,0.1)", border: "1px solid #22c55e",
            borderRadius: 8, padding: 12, marginTop: 8, fontSize: 13
        }}>
            <strong style={{ color: "#22c55e" }}>Issue created</strong>
            <span style={{ marginLeft: 8 }}>
                Assigned to {result.assignedTo}
                {result.emailDeferred ? " — Collated email awaits Close Review." : result.emailSent ? " — email sent" : result.assignedEmail ? " — email failed" : " — no email (not configured)"}
            </span>
        </div>
    );
}

function StoreReports() {
    const { siteId } = useParams();
    const navigate = useNavigate();
    const { accounts } = useMsal();
    const currentAccount = accounts[0];
    const { hasPermission } = useAuth();
    const canCreateIssue = hasPermission('issues.create');
    const [scans, setScans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [storeName, setStoreName] = useState("");
    const [expandedScan, setExpandedScan] = useState(null);
    const [scanDetail, setScanDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [modalImages, setModalImages] = useState(null);
    const [modalIndex, setModalIndex] = useState(0);
    const [selectedDate, setSelectedDate] = useState(null);
    const [creatingIssueFor, setCreatingIssueFor] = useState(null);
    const [issueResults, setIssueResults] = useState({});
    const [activeReviewKey, setActiveReviewKey] = useState(null);
    const [reviewIssueCount, setReviewIssueCount] = useState(0);
    const [reviewCompleting, setReviewCompleting] = useState(false);
    const [scanTriggering, setScanTriggering] = useState(false);
    const [scanMsg, setScanMsg] = useState(null);

    useEffect(() => {
        Promise.all([
            api.scans().catch(() => []),
            api.store(siteId).catch(() => null),
        ]).then(([allScans, storeData]) => {
            const filtered = (allScans || []).filter((s) => String(s.siteId) === String(siteId));
            setScans(filtered);
            if (storeData?.store?.name) setStoreName(storeData.store.name);
            setLoading(false);
        });
    }, [siteId]);

    // Group scans by date
    const scanDates = useMemo(() => {
        const dateMap = {};
        for (const scan of scans) {
            const date = scan.scanHour?.slice(0, 10);
            if (!date) continue;
            if (!dateMap[date]) {
                dateMap[date] = { date, scans: [], totalFindings: 0, criticalCount: 0 };
            }
            dateMap[date].scans.push(scan);
            dateMap[date].totalFindings += scan.findingCount || 0;
            dateMap[date].criticalCount += scan.criticalCount || 0;
        }
        return Object.values(dateMap).sort((a, b) => b.date.localeCompare(a.date));
    }, [scans]);

    const dateScans = useMemo(() => {
        if (!selectedDate) return [];
        return scanDates.find(d => d.date === selectedDate)?.scans || [];
    }, [scanDates, selectedDate]);

    const handleExpandScan = useCallback(async (scan) => {
        const key = `${scan.siteId}/${scan.scanHour}`;
        if (expandedScan === key) {
            setExpandedScan(null);
            setScanDetail(null);
            return;
        }
        setExpandedScan(key);
        setScanDetail(null);
        setDetailLoading(true);
        try {
            const hourParam = scan.scanHour.replace(" ", "+");
            const data = await api.scanDetail(scan.siteId, hourParam);
            setScanDetail(data);
        } catch (err) {
            console.error("Scan detail error:", err);
        } finally {
            setDetailLoading(false);
        }
    }, [expandedScan]);

    const formatTime = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        return d.toLocaleString([], { hour: "2-digit", minute: "2-digit" });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr + "T00:00:00");
        return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
    };

    const handleIssueCreated = (key, result) => {
        setIssueResults(prev => ({ ...prev, [key]: result }));
        setCreatingIssueFor(null);
        if (activeReviewKey) {
            setReviewIssueCount(prev => prev + 1);
        }
    };

    const handleStartReview = async (scan) => {
        const hourParam = scan.scanHour.replace(' ', '+');
        const key = `${scan.siteId}/${scan.scanHour}`;
        try {
            await api.startReview(scan.siteId, hourParam, {
                reviewerEmail: currentAccount?.username || 'web_user',
                reviewerName: currentAccount?.name || currentAccount?.username || 'Web User'
            });
            setActiveReviewKey(key);
            setReviewIssueCount(0);
        } catch (err) {
            console.error('Failed to start review:', err);
        }
    };

    const handleCompleteReview = async (scan) => {
        const hourParam = scan.scanHour.replace(' ', '+');
        setReviewCompleting(true);
        try {
            await api.completeReview(scan.siteId, hourParam, {
                reviewerEmail: currentAccount?.username || 'web_user',
                issuesRaised: reviewIssueCount
            });
            setActiveReviewKey(null);
            setReviewIssueCount(0);
            navigate('/reviews');
        } catch (err) {
            console.error('Failed to complete review:', err);
        } finally {
            setReviewCompleting(false);
        }
    };

    const handleScanNow = async () => {
        setScanTriggering(true);
        setScanMsg(null);
        try {
            const result = await api.triggerScan(siteId);
            setScanMsg({ type: "success", text: result.message || "Scan queued successfully" });
        } catch (err) {
            setScanMsg({ type: "error", text: err.message || "Failed to trigger scan" });
        } finally {
            setScanTriggering(false);
            setTimeout(() => setScanMsg(null), 3000);
        }
    };

    if (loading) return <LoadingSpinner message="Loading reports..." />;

    return (
        <div>
            <div className="page-header">
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                    <Link to="/stores" style={{ color: "var(--text-muted)", textDecoration: "none", fontSize: 13 }}>Stores</Link>
                    <span style={{ color: "var(--text-muted)" }}>/</span>
                    <Link to={`/store/${siteId}`} style={{ color: "var(--text-muted)", textDecoration: "none", fontSize: 13 }}>{storeName || siteId}</Link>
                    <span style={{ color: "var(--text-muted)" }}>/</span>
                    <span style={{ fontSize: 13 }}>Reports</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>Scan Reports {"\u2014"} {storeName || `Site ${siteId}`}</h2>
                    <button
                        onClick={handleScanNow}
                        disabled={scanTriggering}
                        style={{
                            padding: "6px 16px", fontSize: 13, fontWeight: 600, cursor: scanTriggering ? "not-allowed" : "pointer",
                            border: "1px solid var(--primary)", borderRadius: 6,
                            background: "var(--primary)", color: "#fff",
                            opacity: scanTriggering ? 0.6 : 1,
                        }}
                    >
                        {scanTriggering ? "Triggering..." : "Scan Now"}
                    </button>
                </div>
                <p>{scans.length} scan{scans.length !== 1 ? "s" : ""} recorded</p>
                {scanMsg && (
                    <div style={{
                        padding: "6px 12px", borderRadius: 6, fontSize: 13, marginTop: 4,
                        background: scanMsg.type === "success" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                        color: scanMsg.type === "success" ? "#22c55e" : "#ef4444",
                        border: `1px solid ${scanMsg.type === "success" ? "#22c55e" : "#ef4444"}`,
                    }}>
                        {scanMsg.text}
                    </div>
                )}
            </div>

            {scans.length === 0 ? (
                <div className="empty-state">
                    <h3>No scans yet</h3>
                    <p>Scan reports will appear here once this store starts scanning.</p>
                </div>
            ) : (
                <>
                    {/* Date buttons */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                        {scanDates.map(d => (
                            <button
                                key={d.date}
                                onClick={() => { setSelectedDate(d.date === selectedDate ? null : d.date); setExpandedScan(null); setScanDetail(null); }}
                                style={{
                                    padding: "6px 14px", fontSize: 13, fontWeight: 500, cursor: "pointer",
                                    border: "1px solid " + (selectedDate === d.date ? "var(--accent)" : "var(--border)"),
                                    borderRadius: 6,
                                    background: selectedDate === d.date ? "var(--accent)" : "var(--bg-secondary)",
                                    color: selectedDate === d.date ? "#fff" : "var(--text-primary)",
                                    fontFamily: "var(--font)",
                                }}
                            >
                                {formatDate(d.date)}
                                <span style={{ marginLeft: 6, opacity: 0.7, fontSize: 12 }}>
                                    {d.scans.length} scan{d.scans.length !== 1 ? "s" : ""}
                                </span>
                                {d.totalFindings > 0 && (
                                    <span style={{
                                        marginLeft: 6, fontSize: 11, fontWeight: 700,
                                        color: d.criticalCount > 0 ? "var(--red)" : "#f97316",
                                        background: selectedDate === d.date ? "rgba(255,255,255,0.2)" : "rgba(249,115,22,0.1)",
                                        padding: "1px 6px", borderRadius: 10
                                    }}>
                                        {d.totalFindings}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {!selectedDate ? (
                        <div className="empty-state">
                            <h3>Select a date above</h3>
                            <p>{scanDates.length} day{scanDates.length !== 1 ? "s" : ""} with scans for this store.</p>
                        </div>
                    ) : (
                        <div className="scan-list">
                            {dateScans.map((scan) => {
                                const key = `${scan.siteId}/${scan.scanHour}`;
                                const isExpanded = expandedScan === key;
                                return (
                                    <div key={key} className={`scan-row ${isExpanded ? "expanded" : ""}`}>
                                        <div className="scan-row-header" onClick={() => handleExpandScan(scan)}>
                                            <div className="scan-row-main">
                                                <span className="scan-store-name">{formatTime(scan.scanStart)}</span>
                                            </div>
                                            <div className="scan-row-meta">
                                                <span className="scan-presets">{scan.presetCount} presets</span>
                                                <ScoreBadge score={scan.avgScore} />
                                                {scan.findingCount > 0 && (
                                                    <span className="scan-findings-badge" style={{
                                                        color: scan.criticalCount > 0 ? "var(--red)" : "#f97316"
                                                    }}>
                                                        {scan.findingCount} finding{scan.findingCount !== 1 ? "s" : ""}
                                                    </span>
                                                )}
                                                {scan.reviewStatus === 'completed' && (
                                                    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: "rgba(34,197,94,0.1)", color: "#22c55e" }}>
                                                        Reviewed{scan.reviewerName ? ` by ${scan.reviewerName}` : ''}
                                                    </span>
                                                )}
                                                {scan.reviewStatus === 'in_progress' && (
                                                    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: "rgba(59,130,246,0.1)", color: "#3b82f6" }}>
                                                        Under review{scan.reviewerName ? ` by ${scan.reviewerName}` : ''}
                                                    </span>
                                                )}
                                                <span className="scan-expand-icon">{isExpanded ? "\u25B2" : "\u25BC"}</span>
                                            </div>
                                        </div>

                                        {/* Review action bar */}
                                        {isExpanded && (
                                            <div style={{ display: "flex", gap: 8, padding: "8px 16px", borderBottom: "1px solid var(--border)", alignItems: "center" }}>
                                                {activeReviewKey === key ? (
                                                    <>
                                                        <span style={{ fontSize: 13, color: "#3b82f6", fontWeight: 600 }}>
                                                            Reviewing{reviewIssueCount > 0 ? ` (${reviewIssueCount} issue${reviewIssueCount !== 1 ? 's' : ''} raised)` : ''}
                                                        </span>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleCompleteReview(scan); }}
                                                            disabled={reviewCompleting}
                                                            style={{
                                                                marginLeft: "auto", padding: "5px 14px", fontSize: 12, fontWeight: 600,
                                                                border: "none", borderRadius: 6, cursor: "pointer",
                                                                background: "#22c55e", color: "#fff"
                                                            }}
                                                        >
                                                            {reviewCompleting ? "Completing..." : "Mark as Reviewed"}
                                                        </button>
                                                    </>
                                                ) : scan.reviewStatus !== 'completed' ? (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleStartReview(scan); }}
                                                        style={{
                                                            padding: "5px 14px", fontSize: 12, fontWeight: 600,
                                                            border: "1px solid #3b82f6", borderRadius: 6, cursor: "pointer",
                                                            background: "rgba(59,130,246,0.1)", color: "#3b82f6"
                                                        }}
                                                    >
                                                        I am reviewing this scan
                                                    </button>
                                                ) : (
                                                    <span style={{ fontSize: 13, color: "#22c55e", fontWeight: 500 }}>
                                                        Reviewed{scan.reviewerName ? ` by ${scan.reviewerName}` : ''}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {isExpanded && (
                                            <div className="scan-detail">
                                                {detailLoading ? (
                                                    <LoadingSpinner message="Loading scan details..." />
                                                ) : scanDetail ? (
                                                    <div className="scan-preset-grid">
                                                        {scanDetail.presets.map((p) => {
                                                            const presetKey = `preset:${p.id}`;
                                                            return (
                                                            <div key={p.id} className="scan-preset-card">
                                                                {p.imageUrl ? (
                                                                    <img
                                                                        src={p.imageUrl}
                                                                        alt={p.presetName}
                                                                        className="scan-preset-image"
                                                                        onClick={() => {
                                                                            const allImages = scanDetail.presets
                                                                                .filter(x => x.imageUrl)
                                                                                .map(x => ({ url: x.imageUrl, label: `${x.presetName} - ${x.checkType}` }));
                                                                            const idx = allImages.findIndex(x => x.url === p.imageUrl);
                                                                            setModalImages(allImages);
                                                                            setModalIndex(idx >= 0 ? idx : 0);
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="scan-preset-no-image">No image</div>
                                                                )}
                                                                <div className="scan-preset-info">
                                                                    <div className="scan-preset-header">
                                                                        <strong>{p.presetName}</strong>
                                                                        <ScoreBadge score={p.score} />
                                                                    </div>
                                                                    <div className="scan-preset-type">{p.checkType}</div>
                                                                    {p.summary && (
                                                                        <div className="scan-preset-summary">{p.summary}</div>
                                                                    )}

                                                                    {/* Findings with per-finding Create Issue */}
                                                                    {p.findings.length > 0 && (
                                                                        <div className="scan-preset-findings">
                                                                            {p.findings.map((f) => {
                                                                                const fKey = `finding:${f.id}`;
                                                                                return (
                                                                                <div key={f.id} className="scan-finding-item" style={{ marginBottom: 8 }}>
                                                                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                                                        <span className="scan-finding-severity" style={{
                                                                                            color: severityColor[f.severity] || "var(--text-secondary)"
                                                                                        }}>
                                                                                            {f.severity}
                                                                                        </span>
                                                                                        <span className="scan-finding-title">{f.title}</span>
                                                                                    </div>
                                                                                    {f.description && (
                                                                                        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>
                                                                                            {f.description}
                                                                                        </div>
                                                                                    )}

                                                                                </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}

                                                                    {/* Preset-level Create Issue button */}
                                                                    <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                                                                        {issueResults[presetKey] ? (
                                                                            <IssueCreatedBanner result={issueResults[presetKey]} />
                                                                        ) : creatingIssueFor === presetKey ? (
                                                                            <CreateIssueForm
                                                                                preset={p}
                                                                                siteId={siteId}
                                                                                scanHour={scan.scanHour}
                                                                                onCreated={(result) => handleIssueCreated(presetKey, result)}
                                                                                onCancel={() => setCreatingIssueFor(null)}
                                                                            />
                                                                        ) : canCreateIssue ? (
                                                                            <button
                                                                                className="raise-issue-btn raise-issue-btn-primary"
                                                                                onClick={(e) => { e.stopPropagation(); setCreatingIssueFor(presetKey); }}
                                                                            >
                                                                                Raise Issue
                                                                            </button>
                                                                        ) : null}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <div className="empty-state"><p>Failed to load details</p></div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {modalImages && (
                <ImageModal
                    images={modalImages}
                    currentIndex={modalIndex}
                    onClose={() => setModalImages(null)}
                    onPrev={modalIndex > 0 ? () => setModalIndex(modalIndex - 1) : null}
                    onNext={modalIndex < modalImages.length - 1 ? () => setModalIndex(modalIndex + 1) : null}
                />
            )}
        </div>
    );
}

export default StoreReports;
